import React, { Component } from 'react'
import { Box, Button, Icons, Input, List, Page, Spacer } from 'components'
import { getMatchParams, navigate } from 'shared/router'
import {
  ChangeCodeManagement,
  CustomTmrItem,
  GroupedVariantItems,
  Phase,
  PhaseOrderSupplier,
  PhaseOrderSupplierFilter,
} from 'api/types'
import { __, T } from 'translations/i18n'
import CustomItems from 'api/CustomItems'
import { askUserConfirmation, showToast } from 'shared/utils'
import ChangeCodeApprovalGroupedRow from 'components/ChangeCodeApprovalGroupedRow'
import ChangeCodeApprovalModal from '../../components/modals/ChangeCodeApprovalModal'
import PhaseOrderSuppliers from '../../api/PhaseOrderSuppliers'

interface State {
  orderCode: string
  orderId: string
  items: CustomTmrItem[]
  filteredItems: CustomTmrItem[]
  loading: boolean
  disableCheckboxes: boolean
  query: string
  isAllChecked: boolean
  phaseOrderSuppliers: PhaseOrderSupplier[]
  smallestAcceptablePhaseSequence?: number
  madeFor?: string
  showModal: boolean
  dto?: ChangeCodeManagement
  /** list of item id checked */
  checkedList: string[]
}

export default class PackingListCreate extends Component<{}, State> {
  state: State = {
    orderCode: '',
    orderId: '',
    loading: false,
    disableCheckboxes: false,
    query: '',
    isAllChecked: false,
    phaseOrderSuppliers: [],
    items: [],
    filteredItems: [],
    showModal: false,
    smallestAcceptablePhaseSequence: undefined,
    checkedList: [],
  }

  phaseOrderSupplierFilter: PhaseOrderSupplierFilter = {
    asc: false,
    offset: 0,
    limit: 25,
    orderBy: ['CREATION_DATE'],
    orderId: '',
    phaseCodesNotIn: ['1000'],
  }

  navigateBack = () => {
    this.resetState()
    navigate('/change-code/approval')
  }

  async componentDidMount() {
    await this.reloadItems()
    const orderCode = getMatchParams(this.props).orderCode
    this.phaseOrderSupplierFilter = { ...this.phaseOrderSupplierFilter, orderId: this.state.orderId }
    const phaseOrderSuppliers = await PhaseOrderSuppliers.get(this.phaseOrderSupplierFilter)
    this.setState({ loading: false, orderCode, phaseOrderSuppliers }) //order id will be present here
  }

  async reloadPage() {
    this.reloadItems()
    this.setState({ loading: false, showModal: false })
  }

  async reloadItems() {
    const orderCode = getMatchParams(this.props).orderCode
    this.setState({ loading: true })
    const items = await CustomItems.getChangeCodeToApprove(orderCode)
    this.setState({ checkedList: [], items, filteredItems: items, orderId: items[0]?.orderId ?? '' })
  }

  onInputEnter = (input: string) => {
    this.setState({ query: input })
  }

  groupItems = (items: CustomTmrItem[]): GroupedVariantItems => {
    const groupItems = items.reduce((acc, curr) => {
      const keys: string[] = [
        curr.phase?.code,
        curr.executionPhase?.code,
        curr.supplier?.code,
        curr.product.color?.value,
        curr.product.size?.value,
      ]
      const key = keys.map((k) => `${k}`).join('_')

      if (acc[key] && Array.isArray(acc[key])) {
        acc[key].push(curr)
      } else {
        acc[key] = [curr]
      }
      return acc
    }, {} as GroupedVariantItems)
    return groupItems
  }

  filterResults = (result: CustomTmrItem) => {
    const { query } = this.state
    return result.itemIdentifiers
      .find((idf) => idf.identifierType === 'CertilogoIdentifier')
      ?.code.toString()
      .includes(query)
  }

  filterItems = () => {
    const { items, checkedList } = this.state
    const filteredItems = items.filter(this.filterResults)
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      // remove items checked if not visible
      if (!filteredItems.find((fi) => fi.id === item.id)) {
        const idxCheckItem = checkedList.indexOf(item.id)
        if (idxCheckItem !== -1) {
          checkedList.splice(idxCheckItem, 1)
        }
      }
    }
    this.setState({ filteredItems, checkedList })
  }

  handleAllCheck = (checked: boolean, data: string[]) => {
    const { checkedList } = this.state
    this.setState({ disableCheckboxes: true })
    const { items } = this.state
    if (checked) {
      data.forEach((n) => {
        if (!checkedList.includes(n)) checkedList.push(n)
      })
    } else {
      data.forEach((n) => {
        const i = checkedList.indexOf(n)
        checkedList.splice(i, 1)
      })
    }
    this.setState({
      isAllChecked: checkedList.length === items.length,
      disableCheckboxes: false,
      checkedList,
    })
  }

  handleChange = (event: any) => {
    const { items, checkedList } = this.state
    let isAllChecked = false
    if (event.target.checked) {
      checkedList.push(event.target.value)
      isAllChecked = checkedList.length === items.length
    } else {
      const idx = checkedList.indexOf(event.target.value)
      checkedList.splice(idx, 1)
    }
    this.setState({ isAllChecked, checkedList })
  }

  handleChangeGroup = (event: any) => {
    const { items, checkedList } = this.state
    let isAllChecked = false
    const checkGroup = event.target.checked
    const idsGroupValue: string = event.target.value
    const idsGroup = idsGroupValue?.split(',')
    if (!Array.isArray(idsGroup)) return
    for (let i = 0; i < idsGroup.length; i++) {
      const id = idsGroup[i]
      const alreadyPresentIdx = checkedList.indexOf(id)
      if (checkGroup) {
        // must add if not available
        if (alreadyPresentIdx === -1) {
          checkedList.push(id)
        }
      } else if (alreadyPresentIdx !== -1) {
        // must remove if available
        checkedList.splice(alreadyPresentIdx, 1)
      }
    }
    isAllChecked = checkedList.length === items.length
    this.setState({ isAllChecked, checkedList })
  }

  onClickExecutionPhaseChange = () => {
    const { checkedList } = this.state
    if (checkedList.length === 0) {
      showToast({
        title: __(T.error.error),
        description: __(T.error.item_not_selected),
        status: 'error',
      })
      return
    }

    const { items, phaseOrderSuppliers } = this.state
    const itemPhases = items.filter((item) => checkedList.includes(item.id)).map((item) => item?.phase?.code)
    const smallestAcceptablePhaseSequence = phaseOrderSuppliers
      .filter((ph) => itemPhases.includes(ph.phase?.code) && !ph.skipAvanzamento)
      .sort((a, b) => b.sequenzaAvanzamento - a.sequenzaAvanzamento)

    if (smallestAcceptablePhaseSequence.length === 0) {
      showToast({
        title: __(T.error.error),
        description: __(T.error.no_more_execution_phase),
        status: 'error',
      })
      return
    }
    this.setState({
      showModal: true,
      smallestAcceptablePhaseSequence: smallestAcceptablePhaseSequence[0].sequenzaAvanzamento,
    })
  }

  onConfirmExecutionPhaseChange = async (phase?: Phase) => {
    try {
      this.confirmExecutionPhase(phase?.code)
    } catch (e) {
      showToast({ title: __(T.error.error), description: e?.message ?? e, status: 'error' })
    }
  }

  onClickRejectChangeCode = async () => {
    const { checkedList } = this.state
    if (checkedList.length === 0) {
      showToast({
        title: __(T.error.error),
        description: __(T.error.item_not_selected),
        status: 'error',
      })
      return
    }
    const { items } = this.state
    if (checkedList.length === 0) {
      return
    }
    try {
      if (await askUserConfirmation(__(T.confirm.reject_change_code), __(T.messages.confirm_rejection_change_code))) {
        const requestData: ChangeCodeManagement = { itemId: checkedList }
        await CustomItems.rejectChangeCode(requestData)
        showToast({ title: __(T.misc.success), description: __(T.messages.operation_success), status: 'success' })
      } else {
        return
      }
    } catch (e) {
      showToast({ title: __(T.error.error), description: e?.message ?? e, status: 'error' })
    }
    if (items.length === checkedList.length) {
      this.navigateBack()
    } else {
      this.reloadItems()
    }
    this.setState({ loading: false })
  }

  onConfirmApproval = async () => {
    const { checkedList } = this.state
    if (checkedList.length === 0) {
      showToast({
        title: __(T.error.error),
        description: __(T.error.item_not_selected),
        status: 'error',
      })
      return
    }

    if (await askUserConfirmation(__(T.confirm.approve_items), __(T.messages.confirm_execution_phase_message))) {
      this.confirmExecutionPhase()
    }
  }

  confirmExecutionPhase = async (overridePhase?: string) => {
    const { checkedList } = this.state
    const groupBy = (input, key) => {
      return input.reduce((acc, currentValue) => {
        const groupKey = currentValue[key]
        if (!acc[groupKey]) {
          acc[groupKey] = []
        }
        acc[groupKey].push(currentValue)
        return acc
      }, {})
    }

    const groupedItems = groupBy(
      this.state.items
        .filter((item) => checkedList.includes(item.id))
        .map((item) => {
          return { id: item.id, executionPhase: overridePhase ?? item.executionPhase?.code }
        }),
      'executionPhase'
    )

    const requestData: ChangeCodeManagement[] = []
    for (const [key, value] of Object.entries(groupedItems)) {
      const values: any = value
      requestData.push({ itemId: values.map((v) => v.id), phase: key })
    }
    try {
      await CustomItems.approveChangeCode(requestData)
      showToast({ title: __(T.misc.success), description: __(T.messages.exec_phase_success), status: 'success' })
    } catch (err) {
      showToast({
        title: __(T.error.error),
        description: err?.message ?? 'Generic error',
        status: 'error',
      })
    }
    await this.reloadPage()
  }

  resetState = async () => {
    this.setState({
      orderCode: '',
      orderId: '',
      loading: false,
      disableCheckboxes: false,
      query: '',
      isAllChecked: false,
      phaseOrderSuppliers: [],
      showModal: false,
      smallestAcceptablePhaseSequence: undefined,
      checkedList: [],
    })
  }

  onClose = () => {
    this.setState({ showModal: false })
  }

  render() {
    const {
      loading,
      disableCheckboxes,
      isAllChecked,
      orderCode,
      items,
      showModal,
      phaseOrderSuppliers,
      smallestAcceptablePhaseSequence,
      checkedList,
      filteredItems,
    } = this.state
    const grouped = this.groupItems(filteredItems)

    return (
      <Page
        loading={loading}
        title={`${__(T.titles.approve_change_code)} / ${__(T.misc.approve)}`}
        header={{
          details: [
            {
              label: __(T.misc.production_order),
              value: orderCode,
            },
          ],
        }}
        onBackPress={() => {
          this.navigateBack()
        }}
      >
        <Page.Content>
          <Page.TopBar>
            <Input
              startFocus
              placeholder={__(T.misc.certilogo)}
              onChange={(query) => this.setState({ query })}
              image={<Icons.Barcode />}
              style={{ width: 350 }}
              onEnter={this.onInputEnter}
            />
            <Spacer />
            <Box flex />
            <Button
              loading={loading}
              title={isAllChecked ? __(T.misc.unselect_all) : __(T.misc.select_all)}
              variant="secondary"
              onClick={() =>
                this.handleAllCheck(
                  !isAllChecked,
                  items.map((n) => n!.id!)
                )
              }
            />
            <Spacer />
            <Button
              variant="severe"
              loading={loading}
              title={__(T.confirm.reject_change_code)}
              onClick={this.onClickRejectChangeCode}
            />
            <Spacer />
            <Button
              variant="error"
              loading={loading}
              title={__(T.confirm.change_exec_phase)}
              onClick={this.onClickExecutionPhaseChange}
            />
            <Spacer />
            <Button loading={loading} title={__(T.confirm.approve_items)} onClick={this.onConfirmApproval} />
          </Page.TopBar>
          <List
            loading={loading}
            title={`${__(T.titles.change_code)}`}
            renderItem={(i) => (
              <ChangeCodeApprovalGroupedRow
                disabled={disableCheckboxes}
                items={i}
                checkboxHandleChange={this.handleChange}
                checkboxHandleChangeGroup={this.handleChangeGroup}
                checkboxLabel={__(T.misc.select)}
                checkboxLabelGroup={__(T.misc.select)}
                checkedIds={checkedList}
              />
            )}
            data={Object.values(grouped)}
          />
          <ChangeCodeApprovalModal
            title={__(T.confirm.change_exec_phase)}
            onConfirm={this.onConfirmExecutionPhaseChange}
            onClose={this.onClose}
            phaseOrderSuppliers={phaseOrderSuppliers}
            isOpen={showModal}
            smallestAcceptablePhaseSequence={smallestAcceptablePhaseSequence}
          />
        </Page.Content>
      </Page>
    )
  }
}
